/**
 * Notification Components
 * Version: 6.2.0
 * 
 * Bell, dropdown, and toast components
 */
import { useRef, useEffect } from 'react'
import { Notification, NotificationCategory, formatNotificationTime } from '../types/notifications'
import './Notifications.css'

// ============================================
// Notification Bell
// ============================================
interface NotificationBellProps {
  unreadCount: number
  onClick: () => void
  isOpen: boolean
}

export function NotificationBell({ unreadCount, onClick, isOpen }: NotificationBellProps) {
  return (
    <button className={`notification-bell ${isOpen ? 'active' : ''}`} onClick={onClick}>
      <span className="bell-icon">🔔</span>
      {unreadCount > 0 && (
        <span className={`bell-badge ${unreadCount > 9 ? 'large' : ''}`}>
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  )
}

// ============================================
// Category Badge
// ============================================
interface CategoryBadgeProps {
  category: NotificationCategory
}

export function CategoryBadge({ category }: CategoryBadgeProps) {
  const icons: Record<NotificationCategory, string> = {
    [NotificationCategory.TRADE]: '📈',
    [NotificationCategory.ALERT]: '⚠️',
    [NotificationCategory.SYSTEM]: '🔧',
    [NotificationCategory.BOT]: '🤖',
    [NotificationCategory.PORTFOLIO]: '💼',
    [NotificationCategory.MARKET]: '📊',
  }

  return (
    <span className="category-badge">
      {icons[category]}
    </span>
  )
}

// ============================================
// Notification Item
// ============================================
interface NotificationItemProps {
  notification: Notification
  onMarkRead: (id: string) => void
  onDismiss: (id: string) => void
  onClick?: (notification: Notification) => void
}

export function NotificationItem({ notification, onMarkRead, onDismiss, onClick }: NotificationItemProps) {
  const priorityClass = `priority-${notification.priority}`
  const data = notification.data as Record<string, any>
  
  const handleClick = () => {
    if (onClick) onClick(notification)
    if (notification.status === 'unread') {
      onMarkRead(notification.id)
    }
  }

  return (
    <div 
      className={`notification-item ${priorityClass} ${notification.status === 'unread' ? 'unread' : ''}`}
      onClick={handleClick}
    >
      <div className="notification-icon">
        {notification.icon || '🔔'}
      </div>
      <div className="notification-content">
        <div className="notification-header">
          <span className="notification-title">{notification.title}</span>
          <span className="notification-time">{formatNotificationTime(notification.timestamp)}</span>
        </div>
        <p className="notification-message">{notification.message}</p>
        {data && (
          <div className="notification-data">
            {data.symbol && (
              <span className="data-tag">{data.symbol}</span>
            )}
            {data.type && (
              <span className={`data-tag type-${data.type.toLowerCase()}`}>
                {data.type}
              </span>
            )}
            {data.pnl !== undefined && (
              <span className={`data-tag pnl ${data.pnl >= 0 ? 'profit' : 'loss'}`}>
                {data.pnl >= 0 ? '+' : ''}₹{data.pnl}
              </span>
            )}
          </div>
        )}
      </div>
      <button 
        className="notification-dismiss"
        onClick={(e) => {
          e.stopPropagation()
          onDismiss(notification.id)
        }}
      >
        ✕
      </button>
    </div>
  )
}

// ============================================
// Notification Dropdown
// ============================================
interface NotificationDropdownProps {
  notifications: Notification[]
  isOpen: boolean
  onClose: () => void
  onMarkRead: (id: string) => void
  onMarkAllRead: () => void
  onDismiss: (id: string) => void
  onClearAll: () => void
}

export function NotificationDropdown({
  notifications,
  isOpen,
  onClose,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onClearAll,
}: NotificationDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <div className="dropdown-header">
        <h3>Notifications</h3>
        <div className="dropdown-actions">
          <button onClick={onMarkAllRead} className="action-btn">Mark all read</button>
          <button onClick={onClearAll} className="action-btn danger">Clear all</button>
        </div>
      </div>

      <div className="dropdown-content">
        {notifications.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🔕</span>
            <p>No notifications</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={onMarkRead}
              onDismiss={onDismiss}
            />
          ))
        )}
      </div>

      <div className="dropdown-footer">
        <span className="footer-text">{notifications.length} notifications</span>
      </div>
    </div>
  )
}

// ============================================
// Notification Toast
// ============================================
interface NotificationToastProps {
  notification: Notification
  onDismiss: (id: string) => void
  onClick?: (notification: Notification) => void
}

export function NotificationToast({ notification, onDismiss, onClick }: NotificationToastProps) {
  const priorityClass = `toast-${notification.priority}`

  return (
    <div className={`notification-toast ${priorityClass}`} onClick={() => onClick?.(notification)}>
      <div className="toast-icon">{notification.icon || '🔔'}</div>
      <div className="toast-content">
        <div className="toast-title">{notification.title}</div>
        <div className="toast-message">{notification.message}</div>
      </div>
      <button className="toast-dismiss" onClick={() => onDismiss(notification.id)}>
        ✕
      </button>
    </div>
  )
}

// ============================================
// Toast Container
// ============================================
interface ToastContainerProps {
  toasts: Notification[]
  onDismiss: (id: string) => void
  onToastClick?: (notification: Notification) => void
}

export function ToastContainer({ toasts, onDismiss, onToastClick }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <NotificationToast
          key={toast.id}
          notification={toast}
          onDismiss={onDismiss}
          onClick={onToastClick}
        />
      ))}
    </div>
  )
}

// ============================================
// Notification Panel (Full Page)
// ============================================
interface NotificationPanelProps {
  notifications: Notification[]
  onMarkRead: (id: string) => void
  onMarkAllRead: () => void
  onDismiss: (id: string) => void
  onClearAll: () => void
  isOpen: boolean
  onClose: () => void
}

export function NotificationPanel({
  notifications,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onClearAll,
  isOpen,
  onClose,
}: NotificationPanelProps) {
  if (!isOpen) return null

  return (
    <div className="notification-panel-overlay" onClick={onClose}>
      <div className="notification-panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel-header">
          <h2>Notifications</h2>
          <div className="panel-actions">
            <button onClick={onMarkAllRead} className="panel-btn">Mark all read</button>
            <button onClick={onClearAll} className="panel-btn danger">Clear all</button>
            <button onClick={onClose} className="panel-btn close">✕</button>
          </div>
        </div>

        <div className="panel-content">
          {notifications.length === 0 ? (
            <div className="empty-state large">
              <span className="empty-icon">🔕</span>
              <p>No notifications yet</p>
              <span className="empty-sub">You're all caught up!</span>
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={onMarkRead}
                onDismiss={onDismiss}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}